"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { AlertTriangle, RefreshCw, Home, Download, FileText } from "lucide-react"
import { ErrorLogger } from "@/lib/error-logger"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [showLogs, setShowLogs] = useState(false)
  const [logs, setLogs] = useState<string>("")

  useEffect(() => {
    console.error("[v0] Global error caught:", error)
    try {
      ErrorLogger.logError("Global Error", error)
    } catch (e) {
      console.error("[v0] Failed to log error:", e)
    }

    const allLogs = ErrorLogger.getLogs()
    setLogs(JSON.stringify(allLogs, null, 2))
  }, [error])

  const downloadLogs = () => {
    const allLogs = ErrorLogger.getLogs()
    const dataStr = JSON.stringify(allLogs, null, 2)
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr)
    const exportFileDefaultName = `error-log-${new Date().toISOString()}.json`
    const linkElement = document.createElement("a")
    linkElement.setAttribute("href", dataUri)
    linkElement.setAttribute("download", exportFileDefaultName)
    linkElement.click()
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900">
      <Card className="max-w-4xl w-full p-8 bg-slate-800 border-red-500">
        <div className="text-center space-y-6">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto" />

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-white">حدث خطأ في التطبيق</h1>
            <p className="text-slate-300">عذراً، حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.</p>
          </div>

          <div className="p-4 bg-red-950 rounded-lg text-right">
            <p className="text-sm font-mono text-red-300 break-all">{error.message || "خطأ غير معروف"}</p>
            {error.stack && (
              <details className="mt-2 text-xs text-red-400">
                <summary className="cursor-pointer">عرض التفاصيل التقنية</summary>
                <pre className="mt-2 text-right overflow-auto max-h-40">{error.stack}</pre>
              </details>
            )}
          </div>

          <div className="space-y-2">
            <Button
              onClick={() => setShowLogs(!showLogs)}
              variant="outline"
              className="w-full border-yellow-600 text-yellow-400 hover:bg-yellow-950"
            >
              <FileText className="h-4 w-4 mr-2" />
              {showLogs ? "إخفاء السجل الكامل" : "عرض السجل الكامل"}
            </Button>

            {showLogs && (
              <div className="p-4 bg-slate-900 rounded-lg max-h-96 overflow-auto">
                <pre className="text-xs text-green-400 text-right whitespace-pre-wrap font-mono">
                  {logs || "لا توجد سجلات"}
                </pre>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Button onClick={reset} className="bg-blue-600 hover:bg-blue-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              إعادة المحاولة
            </Button>

            <Button onClick={downloadLogs} variant="outline" className="border-green-600 text-green-400 bg-transparent">
              <Download className="h-4 w-4 mr-2" />
              تحميل السجل
            </Button>

            <Button onClick={() => (window.location.href = "/")} variant="outline">
              <Home className="h-4 w-4 mr-2" />
              الصفحة الرئيسية
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
